import { createSlice } from "@reduxjs/toolkit";

// Helper to ensure numeric IDs
const ensureNumericId = (id) => {
  if (id === undefined || id === null || id === "undefined") {
    return null;
  }
  return !isNaN(Number(id)) ? Number(id) : id;
};

const validatorAdminSlice = createSlice({
  name: "validatorAdmin",
  initialState: {
    assignedProjects: [],
    assignedFolders: {}, // projectId -> folders mapping
    assignedFiles: {}, // folderId -> files mapping
    selectedAssignedProjectId: null,
    selectedAssignedFolderId: null,
    selectedAssignedFileId: null,
    validators: [], // All validators grouped by language
    selectedFile: {}, // Selected file with language details
    validatorAdminDetails: [], // Details for validator admin view
    assignedValidators: {},
    loading: false,
    error: null,
    sentenceTableData: [], // <-- new state for sentence table
    targetLang: null
  },
  reducers: {
    addAssignedProjects: (state, action) => {
      state.assignedProjects = action.payload;
    },
    addAssignedFolders: (state, action) => {
      const { projectId, folders } = action.payload;
      // Ensure project ID is numeric
      const numericProjectId = ensureNumericId(projectId);

      if (numericProjectId === null) {
        console.error("Invalid project ID:", projectId);
        return;
      }

      // Ensure folder IDs are numeric
      const processedFolders = folders.map(folder => ({
        ...folder,
        folderDetailsId: ensureNumericId(folder.folderDetailsId),
        folderId: ensureNumericId(folder.folderId)
      }));

      state.assignedFolders = {
        ...state.assignedFolders,
        [numericProjectId]: processedFolders,
      };
    },
    addAssignedFiles: (state, action) => {
      const { folderId, files } = action.payload;
      // Ensure folder ID is numeric
      const numericFolderId = ensureNumericId(folderId);

      if (numericFolderId === null) {
        console.error("Invalid folder ID:", folderId);
        return;
      }

      state.assignedFiles = {
        ...state.assignedFiles,
        [numericFolderId]: files,
      };
    },
    setSelectedAssignedProjectId: (state, action) => {
      // Ensure project ID is numeric
      state.selectedAssignedProjectId = ensureNumericId(action.payload);
    },
    addAssignedValidators: (state, action) => {
      const { langId, validatorName } = action.payload;
      state.assignedValidators[langId] = validatorName;
    },
    setSelectedAssignedFolderId: (state, action) => {
      // Ensure folder ID is numeric
      const numericFolderId = ensureNumericId(action.payload);

      if (numericFolderId === null) {
        console.error("Invalid folder ID being set:", action.payload);
        return;
      }

      state.selectedAssignedFolderId = numericFolderId;
      console.log("Set selected folder ID to:", numericFolderId, "type:", typeof numericFolderId);
    },
    setSelectedAssignedFileId: (state, action) => {
      // Ensure file ID is numeric
      state.selectedAssignedFileId = ensureNumericId(action.payload);
    },
    addValidators: (state, action) => {
      state.validators = action.payload;
    },
    addValidatorAdminDetails: (state, action) => {
      state.validatorAdminDetails = action.payload;
    },
    setSelectedFile: (state, action) => {
      state.selectedFile = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    setSelectedTargetId: (state, action) => {
      state.selectedTargetId = action.payload;
    },
    setSentenceTableData: (state, action) => {
      state.sentenceTableData = action.payload;
    },
    setTargetLang: (state, action) => {
      state.targetLang = action.payload;
    },
  },
});

export const {
  assignedFiles,
  addAssignedProjects,
  addAssignedFolders,
  addAssignedFiles,
  setSelectedAssignedProjectId,
  setSelectedAssignedFolderId,
  setSelectedAssignedFileId,
  addValidators,
  addValidatorAdminDetails,
  setSelectedFile,
  setLoading,
  setError,
  setSelectedTargetId,
  setSentenceTableData,
  setTargetLang
} = validatorAdminSlice.actions;

export default validatorAdminSlice.reducer;
